import {useEffect, useRef, useState} from "react";
import {useLeafletContext} from "@react-leaflet/core";
import L from "leaflet";
import "./MapControls.css";
import ReactDOMServer from "react-dom/server";

type PoiKind =
  | "starting"
  | "withdrawRecurrent"
  | "withdrawSpot"
  | "back"
  | "delivery"
  | "return"
  | "placeholder";


const TYPE_CONFIG: Record<
  PoiKind,
  { color: string; label: string; priority: number }
> = {
  starting: { color: "#9e9e9e", label: "Punto partenza", priority: 10 },
  delivery: { color: "#2196f3", label: "Consegna", priority: 20 },
  withdrawRecurrent: { color: "#b30300ff", label: "Ritiro ricorrente", priority: 30 },
  withdrawSpot: { color: "#ff0000ff", label: "Ritiro spot", priority: 40 },
  back: { color: "#fb8c00", label: "Reso", priority: 50 },
  return: { color: "#26c6da", label: "Riconsegna", priority: 60 },
  placeholder: { color: "#fdd835", label: "Segnaposto", priority: 70 },
};

const LegendPin = ({ color }: { color: string }) => (
  <div className="legend-pin" style={{ color }}>
    <div className="legend-pin-circle" style={{ backgroundColor: color }}>
      <div className="legend-pin-hole" />
    </div>
  </div>
);

const LegendPiePin = ({ colors }: { colors: string[] }) => {
  const slice = 100 / colors.length;
  let current = 0;
  const gradient = colors
    .map((c) => {
      const start = current;
      const end = current + slice;
      current = end;
      return `${c} ${start}% ${end}%`;
    })
    .join(",");

  return (
    <div className="legend-pin" style={{ color: TYPE_CONFIG.delivery.color }}>
      <div
        className="legend-pin-circle"
        style={{ backgroundImage: `conic-gradient(${gradient})` }}
      >
        <div className="legend-pin-hole" />
      </div>
    </div>
  );
};

const LegendPinHeavy: React.FC = () => (
  <div className="poi-pin poi-pin-heavy" style={{ color: TYPE_CONFIG.delivery.color }}>
    <div
      className="poi-pin-circle"
      style={{ backgroundColor: TYPE_CONFIG.delivery.color }}
    >
      <div className="poi-pin-hole" />
    </div>
  </div >
);

// Pin con pallino note (usa .poi-pin-note)
const LegendPinNote: React.FC = () => (
  <div className="poi-pin" style={{ color: TYPE_CONFIG.delivery.color }}>
    <div
      className="poi-pin-circle"
      style={{ backgroundColor: TYPE_CONFIG.delivery.color }}
    >
      <div className="poi-pin-hole" />
      <span className="poi-pin-note">!</span>
    </div>
  </div>
);

const LegendPinNumber: React.FC = () => (
  <div className="poi-pin" style={{ color: TYPE_CONFIG.delivery.color }}>
    <div
      className="poi-pin-circle"
      style={{ backgroundColor: TYPE_CONFIG.delivery.color }}
    >
      <div className="poi-pin-hole" />

      {/* numero posizionato come nei marker veri */}
      <span className="poi-pin-number-text">1</span>
    </div>
  </div>
);

export const MapLegend = () => {
  const context = useLeafletContext();
  const legendControl = useRef<L.Control>();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!context.map) return;

    legendControl.current = new L.Control({ position: "topleft" });

    legendControl.current.onAdd = () => {
      const div = L.DomUtil.create("div", "info-legend");

      // Costruzione dinamica dei tipi
      const typeEntries = Object.entries(TYPE_CONFIG)
        .sort((a, b) => a[1].priority - b[1].priority)
        .map(([key, cfg]) => {
          return `
            <div class="legend-item">
              <span class="legend-icon">${ReactDOMServer.renderToString(
            <LegendPin color={cfg.color} />
          )}</span>
              <span>${cfg.label}</span>
            </div>
          `;
        })
        .join("");

      // Extra (peso, note, multi-tipo)
      const extraEntries = `
        <div class="legend-item">
          <span class="legend-icon">${ReactDOMServer.renderToString(
        <LegendPiePin
          colors={[TYPE_CONFIG.delivery.color, TYPE_CONFIG.withdrawSpot.color]}
        />
      )}</span>
          <span>Multi–tipo (torta)</span>
        </div>

        <div class="legend-item">
          <span class="legend-icon">${ReactDOMServer.renderToString(
        <LegendPinHeavy />
      )}</span>
          <span>Extra peso/colli (striscia rossa)</span>
        </div>

        <div class="legend-item">
          <span class="legend-icon">${ReactDOMServer.renderToString(
        <LegendPinNote />
      )}</span>
          <span>Contiene note operative (pallino giallo)</span>
        </div>

        <div class="legend-item">
          <span class="legend-icon">${ReactDOMServer.renderToString(
        <LegendPinNumber />
      )}</span>
          <span>Marker numerato (missioni)</span>
        </div>
      `;

      div.innerHTML = `
        <div class="legend-header">
          <h4>Legenda <span>${show ? "▲" : "▼"}</span></h4>
        </div>

        <div class="legend-container ${show ? "" : "hide-control"}">
          ${typeEntries}
          <hr/>
          ${extraEntries}
        </div>
      `;

      div.onclick = () => setShow((s) => !s);

      return div;
    };

    legendControl.current.addTo(context.map);

    return () => {
      legendControl.current &&
        context.map.removeControl(legendControl.current);
    };
  }, [context.map, show]);

  return null;
};
